import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import type { TenantNotificationSettings } from "@chat-yai/shared/admin-types";
import { notificationsApi } from "@/api/endpoints/notifications";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { useSession } from "@/contexts/SessionContext";
import { canManageContent } from "@/lib/rbac";
import { EmptyState } from "@/components/EmptyState";

interface FormValues {
  notify_email: string;
  daily_digest_enabled: boolean;
  summary_enabled: boolean;
  summary_idle_minutes: number;
  digest_send_hour: number;
}

export function NotificationsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { me } = useSession();
  const role =
    me?.memberships.find((m) => m.tenant_id === me.current_tenant_id)?.role ??
    "viewer";
  const canEdit = canManageContent(role);

  const { register, handleSubmit, reset, watch, formState } = useForm<FormValues>({
    defaultValues: {
      notify_email: "",
      daily_digest_enabled: true,
      summary_enabled: true,
      summary_idle_minutes: 30,
      digest_send_hour: 8,
    },
  });

  const settingsQuery = useQuery({
    queryKey: ["notifications", "settings"],
    queryFn: () => notificationsApi.get(),
  });

  useEffect(() => {
    const data = settingsQuery.data;
    if (!data) return;
    reset({
      notify_email: data.notify_email ?? "",
      daily_digest_enabled: data.daily_digest_enabled,
      summary_enabled: data.summary_enabled,
      summary_idle_minutes: data.summary_idle_minutes,
      digest_send_hour: data.digest_send_hour,
    });
  }, [settingsQuery.data, reset]);

  const saveMutation = useMutation({
    mutationFn: (input: FormValues) =>
      notificationsApi.update({
        notify_email: input.notify_email.trim() === "" ? null : input.notify_email.trim(),
        daily_digest_enabled: input.daily_digest_enabled,
        summary_enabled: input.summary_enabled,
        summary_idle_minutes: Number(input.summary_idle_minutes),
        digest_send_hour: Number(input.digest_send_hour),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(["notifications", "settings"], next);
      toast.push("success", "Notifications updated.");
    },
    onError: (err) => {
      toast.push(
        "error",
        translateError(err instanceof ApiError ? err.code : undefined),
      );
    },
  });

  const idleWatch = watch("summary_idle_minutes");

  if (settingsQuery.isError) {
    return (
      <div className="page" data-testid="notifications-page">
        <EmptyState
          title="Couldn't load notification settings"
          description="Refresh the page or sign back in."
          icon="alert"
        />
      </div>
    );
  }

  const data: TenantNotificationSettings | undefined = settingsQuery.data;
  const fallback = data?.notify_email_fallback;

  return (
    <div className="page" data-testid="notifications-page">
      <div className="page-head">
        <div>
          <div className="page-title">Notifications</div>
          <div className="page-sub">
            Where lead alerts and the daily digest are emailed, and how
            conversation summaries are generated.
          </div>
        </div>
      </div>

      {settingsQuery.isLoading || !data ? (
        <div className="empty">Loading…</div>
      ) : (
        <form
          className="card"
          style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}
          onSubmit={handleSubmit((v) => saveMutation.mutate(v))}
          data-testid="notifications-form"
        >
          <fieldset
            disabled={!canEdit || saveMutation.isPending}
            style={{ border: 0, padding: 0, display: "contents" }}
          >
            <label className="field">
              <span className="field-label">Notification email</span>
              <input
                className="field-input"
                type="email"
                placeholder={
                  fallback ? `Defaults to ${fallback}` : "owner@example.com"
                }
                data-testid="notifications-email"
                {...register("notify_email")}
              />
              <span className="help">
                Leave blank to send to the workspace Owner&apos;s email
                {fallback ? ` (${fallback})` : ""}.
              </span>
            </label>

            <label
              className="field"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexDirection: "row",
              }}
            >
              <input
                type="checkbox"
                data-testid="notifications-lead-immediate"
                {...register("daily_digest_enabled")}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 13 }}>Daily digest email</span>
                <span className="help" style={{ marginTop: 2 }}>
                  One email each morning summarising yesterday&apos;s
                  conversations and new leads.
                </span>
              </div>
            </label>

            <label
              className="field"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexDirection: "row",
              }}
            >
              <input
                type="checkbox"
                data-testid="notifications-summary-enabled"
                {...register("summary_enabled")}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 13 }}>
                  Auto-summarise closed conversations
                </span>
                <span className="help" style={{ marginTop: 2 }}>
                  An LLM writes a 3-sentence summary when a conversation is
                  closed or stays idle.
                </span>
              </div>
            </label>

            <label className="field" style={{ maxWidth: 320 }}>
              <span className="field-label">
                Idle minutes before auto-summary ({idleWatch})
              </span>
              <input
                className="field-input"
                type="range"
                min={10}
                max={120}
                step={5}
                data-testid="notifications-idle-minutes"
                {...register("summary_idle_minutes", { valueAsNumber: true })}
              />
              <span className="help">10–120 minutes.</span>
            </label>

            <label className="field" style={{ maxWidth: 320 }}>
              <span className="field-label">Daily digest send hour (NZ time)</span>
              <select
                className="field-input"
                data-testid="notifications-digest-hour"
                {...register("digest_send_hour", { valueAsNumber: true })}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {h.toString().padStart(2, "0")}:00
                  </option>
                ))}
              </select>
              <span className="help">
                Timezone: {data.timezone}. Cannot be changed in this release.
              </span>
            </label>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="submit"
                className="btn btn-accent btn-sm"
                disabled={!canEdit || !formState.isDirty || saveMutation.isPending}
                data-testid="notifications-save"
              >
                {saveMutation.isPending ? "Saving…" : "Save changes"}
              </button>
              {!canEdit ? (
                <span className="help" style={{ alignSelf: "center" }}>
                  Read-only — Admin or Owner can change these settings.
                </span>
              ) : null}
            </div>
          </fieldset>
        </form>
      )}
    </div>
  );
}
